interface AiSearchOptions {
  mode: string
  contactIds?: string[]
  limit?: number
  threshold?: number
  reason?: boolean
  secondPerson?: boolean
  allowUnsubscribed?: boolean
  /** "discover-contacts" mode only: exclude contacts ineligible for personalized campaigns. */
  applyEligibility?: boolean
  /** "discover-contacts" mode only: minimum AI score (0-100) after scoring. Omit for no floor. */
  minScore?: number
}

interface SearchByTextOptions {
  limit?: number
  threshold?: number
  allowUnsubscribed?: boolean
}

export interface AskAiContactResult {
  id: string
  name: string
  similarity: number
  aiScore?: number
  reason: string | null
}

export interface AskAiBusinessResult {
  id: string
  name: string
  similarity: number
  aiScore?: number
  reason: string | null
}

export function useAiSearch() {
  const searchContactsForBusiness = async (
    businessId: string,
    options: AiSearchOptions,
  ) => {
    const res = await $fetch('/api/v1/ai/search', {
      method: 'POST',
      body: {
        businessId,
        ...options,
      } as Record<string, unknown>,
    })
    return res
  }

  const searchBusinessesForContact = async (
    contactId: string,
    options: Omit<AiSearchOptions, 'contactIds'>,
  ) => {
    const res = await $fetch('/api/v1/ai/search', {
      method: 'POST',
      body: {
        contactId,
        ...options,
      } as Record<string, unknown>,
    })
    return res
  }

  const searchContactsByText = async (
    text: string,
    options: SearchByTextOptions = {},
  ) => {
    const res = await $fetch<{ statusMessage: string; data: AskAiContactResult[] }>(
      '/api/v1/ai/search',
      {
        method: 'POST',
        body: {
          mode: 'discover-contacts-by-text',
          text,
          limit: options.limit ?? 10,
          threshold: options.threshold ?? 0.3,
          reason: true,
          ...(options.allowUnsubscribed === false && { allowUnsubscribed: false }),
        },
      },
    )
    return res
  }

  const searchBusinessesByText = async (
    text: string,
    options: SearchByTextOptions = {},
  ) => {
    const res = await $fetch<{ statusMessage: string; data: AskAiBusinessResult[] }>(
      '/api/v1/ai/search',
      {
        method: 'POST',
        body: {
          mode: 'discover-businesses-by-text',
          text,
          limit: options.limit ?? 10,
          threshold: options.threshold ?? 0.3,
          reason: true,
        },
      },
    )
    return res
  }

  return {
    searchContactsForBusiness,
    searchBusinessesForContact,
    searchContactsByText,
    searchBusinessesByText,
  }
}
